import SearchIcon from '@mui/icons-material/Search';
import {
  Autocomplete,
  CircularProgress,
  InputAdornment,
  TextField,
} from '@mui/material';
import debounce from 'lodash/debounce';
import { useEffect, useMemo, useState } from 'react';

interface NominatimResult {
  display_name: string;
  lat: string;
  lon: string;
}

interface AddressSearchProps {
  onPlaceSelected: (lat: number, lon: number) => void;
}

export function AddressSearch({ onPlaceSelected }: AddressSearchProps) {
  const [options, setOptions] = useState<NominatimResult[]>([]);
  const [loading, setLoading] = useState(false);

  const search = useMemo(
    () =>
      debounce(async (query: string) => {
        if (!query.trim()) {
          setOptions([]);
          setLoading(false);
          return;
        }
        setLoading(true);
        try {
          // NOTE: Browsers silently drop the User-Agent header (Fetch spec forbidden header).
          // Nominatim will not see this value. Proxying via Django would be needed to properly
          // identify requests. The debounce (300ms) partially mitigates ToS concerns.
          const resp = await fetch(
            `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=5`,
            { headers: { 'User-Agent': 'CSI-App/1.0 (climatesolutionsintl.org)' } },
          );
          setOptions(await resp.json());
        } catch {
          setOptions([]);
        } finally {
          setLoading(false);
        }
      }, 300),
    [],
  );

  useEffect(() => {
    return () => { search.cancel(); };
  }, [search]);

  return (
    <Autocomplete<NominatimResult>
      options={options}
      getOptionLabel={(opt) => opt.display_name}
      filterOptions={(x) => x}
      onInputChange={(_, val) => search(val)}
      onChange={(_, val) => {
        if (val) onPlaceSelected(parseFloat(val.lat), parseFloat(val.lon));
      }}
      loading={loading}
      forcePopupIcon={false}
      noOptionsText="No results"
      isOptionEqualToValue={(opt, val) => opt.display_name === val.display_name}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          placeholder="Search address..."
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading && <CircularProgress size={14} color="inherit" />}
                <InputAdornment position="end" sx={{ marginLeft: 0, marginRight: '4px' }}>
                  <SearchIcon
                    sx={{
                      color: 'rgba(0, 61, 54, 1)',
                      fontSize: 'clamp(16px, 1.3vw, 25px)',
                    }}
                  />
                </InputAdornment>
              </>
            ),
          }}
          sx={{
            width: 'clamp(160px, 13vw, 210px)',
            '& .MuiOutlinedInput-root': {
              backgroundColor: 'rgba(213, 228, 213, 1)',
              color: 'rgba(14, 70, 63, 1)',
              fontSize: 'clamp(10px, 0.8vw, 15px)',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 400,
              borderRadius: '0 !important',
              height: 'clamp(20px, 1.6vw, 30px) !important',
              paddingRight: 'clamp(2px, 0.3vw, 6px)',
              '& fieldset': {
                borderColor: 'rgba(14, 70, 63, 1)',
                borderWidth: '1px',
                borderRadius: 0,
              },
              '&:hover fieldset': {
                borderColor: 'rgba(14, 70, 63, 1)',
                borderWidth: '1px',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'rgba(14, 70, 63, 1)',
                borderWidth: '1px',
              },
            },
            '& .MuiOutlinedInput-input': {
              padding: 'clamp(2px, 0.35vw, 6px) clamp(6px, 0.8vw, 14px)',
              '&::placeholder': {
                color: 'rgba(14, 70, 63, 1)',
                opacity: 1,
                fontFamily: 'Inter, sans-serif',
                fontWeight: 400,
              },
            },
          }}
        />
      )}
    />
  );
}
